import React from "react";

import { DeploymentService } from "@packages/client";
import { AutoscalingIcon } from "@packages/ds";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { Link, Button } from "ds/Button";
import Dialog from "ds/Dialog";
import useDecodedParams from "Hooks/useDecodedParams";
import getUrl from "Libs/getUrl";
import { selectFeatureAutoscalingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import * as S from "./styles";

type LabeledInfoProps = {
  gridColumn?: string;
  label: React.ReactNode;
  infoDialog?: boolean;
  infoDialogAlign?: "center" | "start" | "end";
  infoDialogTitle?: string;
  infoDialogText?: string;
  infoDialogLinkText?: string;
  infoDialogLinkTo?: string;
  value: React.ReactNode;
  shouldCapitalize?: boolean;
  hasAutoscaling?: boolean;
  service?: DeploymentService;
  showAutoscalingDialog?: boolean;
};
const LabeledInfo = ({
  gridColumn = "auto / span 3",
  label,
  value,
  infoDialog = false,
  infoDialogAlign = "center",
  infoDialogTitle,
  infoDialogText,
  infoDialogLinkText,
  infoDialogLinkTo,
  shouldCapitalize = true,
  hasAutoscaling = false,
  showAutoscalingDialog = false,
  service
}: LabeledInfoProps) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { organizationId, projectId, environmentId } = useDecodedParams();

  const showAutoscalingIcon = !!environmentId && hasAutoscaling;

  const autoscalingEnabledFlag = useAppSelector(
    selectFeatureAutoscalingEnabled
  );

  return (
    <S.Layout gridColumn={gridColumn}>
      <S.LabelWrapper>
        <S.Value $color="ebony" shouldCapitalize={shouldCapitalize}>
          {value}
        </S.Value>

        {autoscalingEnabledFlag &&
          showAutoscalingIcon &&
          (showAutoscalingDialog ? (
            <Dialog
              title={intl.formatMessage({
                id: "autoscaling.instances.badge.dialog.title"
              })}
              description={intl.formatMessage({
                id: "autoscaling.instances.badge.dialog.description"
              })}
              trigger={
                <div>
                  <AutoscalingIcon />
                </div>
              }
              link={
                <Button
                  variant="outline"
                  onClick={() => {
                    const autoscaleConfigUrl = getUrl({
                      key: "organization.project.environment.configureAutoscaling",
                      props: {
                        organizationId,
                        projectId,
                        environmentId: environmentId || "",
                        location: location.pathname,
                        queryParams: ""
                      }
                    });
                    navigate(autoscaleConfigUrl, {
                      state: { service }
                    });
                  }}
                >
                  {intl.formatMessage({
                    id: "configure.thresholds"
                  })}
                </Button>
              }
            />
          ) : (
            <div>
              <AutoscalingIcon />
            </div>
          ))}
      </S.LabelWrapper>
      <S.Label>
        {label}
        {infoDialog && (
          <Dialog
            align={infoDialogAlign}
            title={infoDialogTitle}
            description={infoDialogText}
            {...(infoDialogLinkTo &&
              infoDialogLinkText && {
                link: <Link href={infoDialogLinkTo}>{infoDialogLinkText}</Link>
              })}
            analyticId={infoDialogText}
          />
        )}
      </S.Label>
    </S.Layout>
  );
};

export default LabeledInfo;
